import React, { FC, useCallback, useState } from 'react';
import { ColorSelectProps } from '@components/v2/ColorSelect/index';
import { useConfiguratorDuck } from '@hooks/useConfiguratorDuck';
import { useTranslations } from '@hooks/useTranslations';
import { useUILoadingOnConfigurator } from '@hooks/useUILoadingOnConfigurator';
import { IFeature, IPrice, IPriceV2Types } from '../../../services';
import { useAccessoryMonthlyPrices } from '@hooks/useAccessoryPrices';
import { PRICE_TYPE_EMPLOYEE } from '../../../services/filters/filters';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import { isMarketGB } from '../../../constants/main';
import { useJourneyType } from '@hooks/useJourneyType';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import ConfiguratorDuck from '../../../redux/configurator/configurator.duck';
import { useDispatch } from 'react-redux';
import { useGTM } from '@hooks/useGTM';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useUIDuck } from '@hooks/useUIDuck';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import { ImageViewType } from '../../../constants/enums';
import Carousel from 'react-multi-carousel';
import Spinner from '@components/Spinner';
import { sortByPrice } from '@utils/Color.utils';

const responsive = {
    desktop: {
        breakpoint: { max: 4000, min: 1024 },
        items: 5,
    },
    tablet: {
        breakpoint: { max: 1024, min: 560 },
        items: 8,
    },
    mobile: {
        breakpoint: { max: 560, min: 0 },
        items: 5,
    },
};

export const ColorSelectTemplate: FC<ColorSelectProps> = ({ className }) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { pushUserAction } = useGTM();
    const { getFeatureMonthlyPrice } = useAccessoryMonthlyPrices();
    const { isFinance } = useJourneyType();
    const { exteriorColor, exteriorColors } = useConfiguratorDuck();
    const { carDetails } = useCarDetailsDuck();
    const { uiLoadingDisabledClass, uiIsLoading } = useUILoadingOnConfigurator();
    const { isLoading } = useUIDuck();
    const { formatConfiguratorPrice } = useMoneyFormatter();
    const isEmployeePrice = (price: IPrice) => price.type === PRICE_TYPE_EMPLOYEE;

    const images = carDetails?.images?.filter((img: any) => img.type === 'Ext') ?? [{}];
    const mainImage = images?.splice(1, 1) ?? [{}];

    const getPrice = (item: IFeature) => {
        const featureMonthlyPrice = getFeatureMonthlyPrice(item);
        const price = item.prices.find(isEmployeePrice);
        const monthlyPrice = featureMonthlyPrice?.price ?? featureMonthlyPrice?.defaultPrice;
        const basePrice = price.basePrice;
        return { monthlyPrice: monthlyPrice ?? 0, basePrice: basePrice ?? 0 };
    };

    const { monthlyPrice, basePrice } = getPrice(carDetails?.exteriorColour);

    const isBudgetChangeAllowed = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_BUDGET_CHANGE_ALLOWED);
    const hideCashPayment = !isBudgetChangeAllowed && isFinance;
    const showExtendedPrice = !isMarketGB || (isMarketGB && !hideCashPayment);

    const onClick = useCallback(
        (color) => {
            if (!uiIsLoading) {
                pushUserAction({
                    action: 'Choose::Color',
                    eventName: 'uaevent',
                    label: color.title,
                });
                dispatch(ConfiguratorDuck.setExteriorColor(color));
                dispatch(UIDuck.toggleImageViewType(ImageViewType.EXTERIOR));
            }
        },
        [exteriorColor, uiIsLoading, dispatch]
    );

    const scrollToLeftOrRight = () => {
        const elem = document.getElementsByClassName('color')[0];
        elem.scrollLeft = 0;
    };

    const [carouselMoved, setCarouselMoved] = useState(false);

    return (
        <div className={className}>
            <span className="sectionTitle" data-testid="TESTING_CONFIG_COLOR_SELECT_TITLE">
                {t('configurator.colors.title', {
                    count: exteriorColors.length,
                })}
            </span>
            {mainImage && <img className="carImage" src={mainImage[0]?.url} />}
            {isLoading && <Spinner size={36} border={2} color="black" />}
            <div className={`colors ${carouselMoved ? 'moved' : ''} ${isLoading ? 'isLoading' : ''}`}>
                <Carousel
                    responsive={responsive}
                    arrows={true}
                    centerMode={true}
                    slidesToSlide={2}
                    containerClass={`color-item-wrapper ${
                        exteriorColors.length > 5 ? 'color-item-wrapper-infinity' : ''
                    }`}
                    afterChange={() => {
                        setCarouselMoved(!carouselMoved);
                    }}
                >
                    {sortByPrice(exteriorColors).map((clr, index) => {
                        if (!clr.isAvailable) {
                            return (
                                <div key={clr.id} className="disabled" id={`TESTING_COLOR_${clr.id}`}>
                                    <div
                                        className="colorButton disabled"
                                        style={{ backgroundImage: `url('${clr.images[0]?.url}')` }}
                                    ></div>
                                    <div className="tooltip">{t('configurator.incompatibleColor')}</div>
                                </div>
                            );
                        } else {
                            return (
                                <button
                                    key={clr.id}
                                    className={`colorButton enabled ${
                                        exteriorColor && clr.id === exteriorColor.id ? 'is-selected' : ''
                                    } ${uiLoadingDisabledClass}`}
                                    onClick={() => onClick(clr)}
                                    role="button"
                                    data-testid={`TESTING_COLOR_${index}`}
                                    aria-label={exteriorColor.title}
                                    style={{ backgroundImage: `url('${clr.images[0]?.url}')` }}
                                />
                            );
                        }
                    })}
                </Carousel>
                <span className="name" data-testid={`TESTING_COLOR_NAME`}>
                    {exteriorColor.title}
                </span>
                <span className="price" data-testid={`TESTING_COLOR_PRICE`}>
                    {showExtendedPrice &&
                        basePrice > 0 &&
                        formatConfiguratorPrice(
                            basePrice,
                            '+ ',
                            true,
                            '',
                            null,
                            monthlyPrice ? t('configurator.optionPrice.suffix') : ''
                        )}
                    {monthlyPrice > 0 && (
                        <>
                            {formatConfiguratorPrice(monthlyPrice, '+ ', true, '', !isFinance)}
                            <span> {t('car.price.monthlyPriceLabel').toUpperCase()}</span>
                        </>
                    )}
                    {basePrice === 0 && monthlyPrice === 0 && t('configurator.colors.includedInPrice')}
                </span>
            </div>
        </div>
    );
};
